// İlişki (relation) seçicileri için tek merkezde config + veri-çekme fabrikaları.
// RelationSelect/RelationPicker ile kullanılır. Minimal kolon seti: Kod + (Ad/Açıklama).
//
// Her config:
//   endpoint     : OData taban yolu (ör. '/api/customers')
//   mode         : 'client' (orta lookup, tek seferde çek) | 'server' (büyük liste, sayfalı)
//   columns      : [{ key, header, type, odataField }]  (datagrid + filtre/sort)
//   searchFields : genel aramada taranan OData alanları (ör. ['TextId','Title'])
//   orderBy      : varsayılan sıralama alanı (OData)
//   light        : true ise URL'e light=1 (ağır kolonları dışla, ör. ürün görseli)
//   title        : panel başlığı
//   getRowLabel  : seçili gösterim etiketi

import {
  authHeaders,
  buildODataUrl,
  getODataRows,
  getExplicitODataTotalCount,
  resolveODataTotalCount,
} from './listPageStyles';
import { buildFilterClause, escapeODataString } from './c360/filterModel';

// Kod + (ünvan/ad/açıklama) genel etiket — OrderHeadersPage.lookupLabel ile aynı mantık.
export function relationLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || item.erpId || item.ErpId || '';
  const desc = item.title || item.Title || item.name || item.Name
    || item.shortName || item.ShortName || item.description || item.Description || '';
  return desc ? `${code} - ${desc}`.replace(/^ - /, '') : code;
}

const codeCol = (header = 'Kod') => ({ key: 'textId', header, type: 'text', odataField: 'TextId' });
const descCol = { key: 'description', header: 'Açıklama', type: 'text', odataField: 'Description' };
const nameCol = { key: 'name', header: 'Ad', type: 'text', odataField: 'Name' };
const titleCol = { key: 'title', header: 'Ünvan', type: 'text', odataField: 'Title' };

// #248 Ürün seçicide grup/alt grup gösterimi. Bu bilgi Product satırında düz alan olarak yok;
// yalnız $expand=Group,SubGroup ile nested nesne (camelCase group/subGroup) olarak gelir.
// Grup adı (Description) öncelikli, yoksa kodu (TextId).
const navGroupText = (g) => {
  if (!g) return '';
  return g.description || g.Description || g.textId || g.TextId || '';
};
// Grup kolonları OData nav-alanı üzerinden filtre/sort ettirmeyelim (nav filtrelemesi kırılgan);
// yalnız görüntüleme amaçlı — render ile nested nesneden okunur.
const productGroupCol = {
  key: 'groupText', header: 'Ürün Grubu', type: 'text', filterable: false, sortable: false,
  render: (r) => navGroupText(r.group || r.Group),
};
const productSubGroupCol = {
  key: 'subGroupText', header: 'Ürün Alt Grubu', type: 'text', filterable: false, sortable: false,
  render: (r) => navGroupText(r.subGroup || r.SubGroup),
};

// "Grup Kodu + Açıklama" deseni (CustomerGroup, ProductGroup, ... ortak)
const groupConfig = (endpoint, title) => ({
  endpoint,
  mode: 'client',
  columns: [codeCol('Grup Kodu'), descCol],
  searchFields: ['TextId', 'Description'],
  orderBy: 'TextId',
  title,
  getRowLabel: relationLabel,
});

// "Kod + Ad" deseni (Vendor, Representative, Warehouse, Producer ortak)
const namedConfig = (endpoint, title) => ({
  endpoint,
  mode: 'client',
  columns: [codeCol('Kod'), nameCol],
  searchFields: ['TextId', 'Name'],
  orderBy: 'TextId',
  title,
  getRowLabel: relationLabel,
});

export const RELATION_CONFIGS = {
  // --- Orta boy lookup'lar (istemci modu) ---
  customerGroup: groupConfig('/api/customergroups', 'Müşteri Grubu Seç'),
  customerSubGroup: groupConfig('/api/customersubgroups', 'Müşteri Alt Grubu Seç'),
  customerDynamicGroup: groupConfig('/api/customerdynamicgroups', 'Müşteri Dinamik Grubu Seç'),
  productGroup: groupConfig('/api/productgroups', 'Ürün Grubu Seç'),
  productSubGroup: groupConfig('/api/productsubgroups', 'Ürün Alt Grubu Seç'),
  productDynamicGroup: groupConfig('/api/productdynamicgroups', 'Ürün Dinamik Grubu Seç'),
  producer: namedConfig('/api/producers', 'Üretici Seç'),
  vendor: namedConfig('/api/vendors', 'Distribütör Seç'),
  vendorGroup: groupConfig('/api/vendorgroups', 'Distribütör Grubu Seç'),
  vendorSubGroup: groupConfig('/api/vendorsubgroups', 'Distribütör Alt Grubu Seç'),
  representative: namedConfig('/api/representatives', 'Temsilci Seç'),
  representativeGroup: groupConfig('/api/representativegroups', 'Temsilci Grubu Seç'),
  salesManager: groupConfig('/api/salesmanagers', 'Satış Müdürü Seç'),
  warehouse: namedConfig('/api/warehouses', 'Depo Seç'),
  distributor: namedConfig('/api/distributors', 'Distribütör Seç'),
  // #278 Rol seçici (Kod + Ad; Role'de TextId yerine Code alanı var).
  role: {
    endpoint: '/api/Roles',
    mode: 'client',
    columns: [{ key: 'code', header: 'Kod', type: 'text', odataField: 'Code' }, nameCol],
    searchFields: ['Code', 'Name'],
    orderBy: 'Code',
    title: 'Rol Seç',
    getRowLabel: (item) => {
      const code = item?.code ?? item?.Code ?? '';
      const name = item?.name ?? item?.Name ?? '';
      return name ? (code ? `${code} - ${name}` : name) : String(code);
    },
  },

  // #817 Kullanıcı seçici — Genel Parametreler › "Giriş Gün ve Saat Kısıtı" kriter alanı kullanır.
  // User'da TextId YOKTUR (bkz. Models/App/User.cs); kimlik Ad + Soyad + E-posta üzerinden kurulur.
  // Ekran satırları kendi çektiği listeden (rows prop'u) verir; rows verilmezse makeFetchRows
  // devreye girer ve aynı kolonlar OData'dan camelCase gelir.
  user: {
    endpoint: '/api/Users',
    mode: 'client',
    columns: [
      { key: 'firstName', header: 'Ad', type: 'text' },
      { key: 'lastName', header: 'Soyad', type: 'text' },
      { key: 'email', header: 'E-posta', type: 'text' },
    ],
    searchFields: ['FirstName', 'LastName', 'Email'],
    orderBy: 'FirstName',
    title: 'Kullanıcı Seç',
    getRowLabel: (item) => {
      if (!item) return '';
      const full = `${item.firstName ?? item.FirstName ?? ''} ${item.lastName ?? item.LastName ?? ''}`.trim();
      return full || String(item.email ?? item.Email ?? item.id ?? item.Id ?? '');
    },
  },
  // #817 Kullanıcı grubu seçici (KK-04 — kod + ad taşıyan düz sınıflandırma tanımı).
  userGroup: {
    endpoint: '/api/user-groups',
    mode: 'client',
    columns: [
      { key: 'code', header: 'Grup Kodu', type: 'text' },
      { key: 'name', header: 'Ad', type: 'text' },
    ],
    searchFields: ['Code', 'Name'],
    orderBy: 'Code',
    title: 'Kullanıcı Grubu Seç',
    getRowLabel: (item) => {
      const code = item?.code ?? item?.Code ?? '';
      const name = item?.name ?? item?.Name ?? '';
      return name ? (code ? `${code} - ${name}` : name) : String(code);
    },
  },

  // --- Büyük listeler (sunucu modu) ---
  customer: {
    endpoint: '/api/customers',
    mode: 'server',
    columns: [codeCol('Müşteri Kodu'), titleCol],
    searchFields: ['TextId', 'Title'],
    orderBy: 'TextId',
    title: 'Müşteri Seç',
    getRowLabel: relationLabel,
  },
  product: {
    endpoint: '/api/products',
    mode: 'server',
    light: true,
    columns: [codeCol('Ürün Kodu'), nameCol],
    searchFields: ['TextId', 'Name'],
    orderBy: 'TextId',
    title: 'Ürün Seç',
    getRowLabel: relationLabel,
  },
  // #248 Belge kalemi (Fatura/Sipariş/İrsaliye) ürün seçici: Kod + Ad + Ürün Grubu + Ürün Alt Grubu.
  // $expand=Group,SubGroup ile grup adları getirilir (light=1 yalnız görseli atar, grubu değil).
  productWithGroups: {
    endpoint: '/api/products',
    mode: 'server',
    light: true,
    expand: 'Group,SubGroup',
    columns: [codeCol('Ürün Kodu'), nameCol, productGroupCol, productSubGroupCol],
    searchFields: ['TextId', 'Name'],
    orderBy: 'TextId',
    title: 'Ürün Seç',
    getRowLabel: relationLabel,
  },
};

// İstemci modu: tüm listeyi tek seferde OData'dan çek (RelationSelect cache'ler).
export function makeFetchRows(config) {
  const { endpoint, orderBy = 'TextId', light, top = 1000 } = config;
  return async () => {
    let url = `${endpoint}?$orderby=${encodeURIComponent(orderBy)}&$top=${top}`;
    if (light) url += '&light=1';
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) throw new Error('Veri alınamadı');
    return getODataRows(await res.json());
  };
}

// Sunucu modu: arama + kolon filtresi + sıralama + sayfalama → tek OData sorgusu.
export function makeFetchPage(config) {
  const { endpoint, columns, searchFields = [], orderBy = 'TextId', light, baseFilter, expand } = config;
  const colMeta = Object.fromEntries(
    columns.map((c) => [c.key, { odataField: c.odataField || c.key, type: c.type || 'text' }]),
  );
  return async ({ search, columnFilters, sort, page, pageSize }) => {
    const clauses = [];
    // #252 Sabit taban filtre (ör. pasif ürünleri gizle: 'Status ne 0').
    if (baseFilter) clauses.push(`(${baseFilter})`);
    const q = (search || '').trim();
    if (q && searchFields.length) {
      const esc = escapeODataString(q).toLowerCase();
      clauses.push(`(${searchFields.map((f) => `contains(tolower(${f}),'${esc}')`).join(' or ')})`);
    }
    Object.entries(columnFilters || {}).forEach(([key, filter]) => {
      const meta = colMeta[key];
      if (!meta) return;
      const clause = buildFilterClause(meta, filter);
      if (clause) clauses.push(clause);
    });
    const filterParams = clauses.length ? clauses.join(' and ') : undefined;
    const sortField = sort?.key ? (colMeta[sort.key]?.odataField || orderBy) : orderBy;
    const sortDir = sort?.dir === 'desc' ? 'desc' : 'asc';
    let url = buildODataUrl(endpoint, { pageSize, currentPage: page, sortField, sortDir, filterParams, expand });
    if (light) url += '&light=1';
    const headers = authHeaders();
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error('Veri alınamadı');
    const data = await res.json();
    const rows = getODataRows(data);
    let total = getExplicitODataTotalCount(data);
    if (total == null) {
      total = await resolveODataTotalCount({ data, rows, currentPage: page, pageSize, base: endpoint, filterParams, headers });
    }
    return { rows, total };
  };
}
